/** Carrier profili (eskiz 13): KPI, ochiq vakansiyalar, rad sabablari, nomzodlar. */
import { usePageSize } from "@/lib/pageSize";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ApiError } from "@/lib/api";
import { formatDate, formatWhen, initialsOf } from "@/lib/utils";
import { useMe } from "@/features/auth/hooks";
import { recordVisit } from "@/features/widgets/recent/store";
import { can, type Me } from "@/features/auth/types";
import { useCarrier, useCarrierCandidates, useCarrierJobs, useCarrierRejectionReasons, useUpdateCarrier } from "@/features/carriers/api";
import { experienceLabel, statesLabel, type Carrier, type CarrierStatus, type Job } from "@/features/carriers/types";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, KpiCard } from "@/components/ui/card";
import { StatusPill } from "@/components/ui/badge";
import { Modal } from "@/components/ui/modal";
import { Tabs } from "@/components/ui/tabs";
import { Pagination, Table, Td, Th, Tr } from "@/components/ui/table";
import { Avatar, EmptyState, ErrorState, Kv, PageLoading } from "@/components/ui/misc";
import { toast } from "@/components/ui/toast";
import { ContextMenu, type MenuEntry } from "@/components/ui/context-menu";
import { absoluteUrl, copyText, openInNewTab } from "@/lib/clipboard";
import { candidateMenu } from "@/features/carriers/menu";
import { useJobActions } from "@/pages/jobs/useJobActions";
import { JobFormModal } from "@/pages/jobs/JobFormModal";
import { CarrierFormModal } from "./CarrierFormModal";
import { TrucksTab } from "./TrucksTab";
import { ApplicationPill, RejectionRate, SectionTitle, scopeLabel } from "./shared";

type Tab = "overview" | "jobs" | "candidates" | "trucks" | "notes";
const TABS: Tab[] = ["overview", "jobs", "candidates", "trucks", "notes"];

export function CarrierProfilePage() {
  const { id } = useParams();
  const { t } = useTranslation("carriers");
  const me = useMe();
  const carrier = useCarrier(id);
  if (me.isPending || carrier.isPending) return <PageLoading />;
  if (!me.data) return null;
  if (carrier.isError) return <ErrorState message={carrier.error instanceof ApiError && carrier.error.status === 404 ? t("profile.notFound") : carrier.error.message} onRetry={() => carrier.refetch()} />;
  return <Profile me={me.data} carrier={carrier.data} />;
}

function Profile({ me, carrier }: { me: Me; carrier: Carrier }) {
  // Oxirgi ochilganlar widgeti (shu qurilmada)
  useEffect(() => {
    recordVisit({ kind: "carrier", id: carrier.id, title: carrier.name, subtitle: carrier.mc_number ? `MC ${carrier.mc_number}` : "" });
  }, [carrier.id, carrier.name, carrier.mc_number]);
  const jobActions = useJobActions();
  const { t: tm } = useTranslation("menu");
  const { t: ti } = useTranslation("metrics");
  const candidateItems = (a: { driver: { id: number; full_name: string }; job: { id: number; title: string } }) =>
    candidateMenu(a, { navigate: (p) => navigate(p), openNewTab: openInNewTab, copyLink: (p) => void copyText(absoluteUrl(p)) }, tm);
  const { t } = useTranslation("carriers");
  const navigate = useNavigate();
  const manager = can(me, "carriers_edit");
  // ?tab=trucks -- audit va boshqa havolalar kerakli tabni ochadi (driver profili kabi)
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => (TABS.includes(params.get("tab") as Tab) ? (params.get("tab") as Tab) : "overview"));
  const [editOpen, setEditOpen] = useState(false);
  const [jobOpen, setJobOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState<CarrierStatus | null>(null);
  const [candPage, setCandPage] = useState(1);
  const [candPageSize, setCandPageSize] = usePageSize("carrier-candidates");

  const jobs = useCarrierJobs(carrier.id);
  const candidates = useCarrierCandidates(carrier.id, candPage, candPageSize);
  const reasons = useCarrierRejectionReasons(carrier.id);
  const update = useUpdateCarrier(carrier.id);

  const allJobs = jobs.data?.results ?? [];
  const openJobs = allJobs.filter((j) => j.status === "open");
  const hiredPct = carrier.submitted_count ? Math.round((carrier.hired_count * 100) / carrier.submitted_count) : 0;

  async function changeStatus(status: CarrierStatus) {
    try {
      await update.mutateAsync({ status, version: carrier.version });
      toast.success(t(`profile.statusToast.${status}`));
    } catch (err) {
      if (err instanceof ApiError && err.isConflict) toast.error(err.changedBy ? t("common:toast.conflict", { who: err.changedBy }) : t("common:toast.conflictAnon"));
      else toast.error(err instanceof ApiError ? err.message : t("profile.updateFailed"));
    } finally {
      setStatusTarget(null);
    }
  }

  const location = [carrier.city, carrier.state].filter(Boolean).join(", ");

  return (
    <div>
      <div className="mb-3 text-[12px] text-muted">
        <Link to="/carriers" className="hover:underline">
          {t("title")}
        </Link>{" "}
        / <span className="font-semibold text-ink">{carrier.name}</span>
      </div>

      <Card className="mb-4 px-5 py-4">
        <div className="flex items-start gap-4">
          <Avatar initials={initialsOf(carrier.name)} name={carrier.name} size={56} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-[22px] leading-tight font-bold">{carrier.name}</h1>
              <StatusPill status={carrier.status} />
            </div>
            <div className="mt-1 text-[13px] text-ink-soft">
              {[carrier.mc_number, carrier.dot_number, location].filter(Boolean).join(" · ")}
              {" · "}{t("profile.hiringIn", { states: statesLabel(carrier.hiring_states) })}
            </div>
            <div className="mt-1 text-[13px] text-ink-soft">
              {t("profile.mainContact")} <span className="font-semibold text-ink">{carrier.contact_name || "—"}</span>
              {carrier.contact_phone && ` · ${carrier.contact_phone}`}
              {carrier.contact_email && ` · ${carrier.contact_email}`}
            </div>
          </div>
          {manager && (
            <div className="flex shrink-0 gap-2">
              <Button onClick={() => setEditOpen(true)}>{t("common:actions.edit")}</Button>
              {carrier.status === "active" ? (
                <Button onClick={() => setStatusTarget("paused")}>{t("profile.pause")}</Button>
              ) : (
                <Button onClick={() => setStatusTarget("active")}>{t("profile.resume")}</Button>
              )}
              <Button variant="primary" onClick={() => setJobOpen(true)} disabled={carrier.status !== "active"} title={carrier.status !== "active" ? t("profile.resumeFirst") : undefined}>
                {t("profile.newJob")}
              </Button>
            </div>
          )}
        </div>
      </Card>

      <Tabs
        className="mb-4"
        value={tab}
        onChange={(k) => setTab(k as Tab)}
        items={[
          { key: "overview", label: t("profile.tabs.overview") },
          { key: "jobs", label: t("profile.tabs.jobs", { count: jobs.data ? allJobs.length : "…" }) },
          { key: "candidates", label: t("profile.tabs.candidates", { count: candidates.data ? candidates.data.count : "…" }) },
          // Bo'sh truck -- carrierga driver kerak (2026-10-25)
          { key: "trucks", label: carrier.trucks_available ? t("profile.tabs.trucksFree", { count: carrier.trucks_total, free: carrier.trucks_available }) : t("profile.tabs.trucks", { count: carrier.trucks_total }) },
          { key: "notes", label: t("profile.tabs.notes") },
        ]}
      />

      {tab === "overview" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <KpiCard onClick={() => setTab("candidates")} info={ti("carrier.submitted")} label={t("profile.kpi.submitted")} value={carrier.submitted_count} sub={t("profile.kpi.allTime", { scope: scopeLabel(me, t) })} />
            <KpiCard onClick={() => setTab("candidates")} info={ti("carrier.hired")} label={t("profile.kpi.hired")} value={carrier.hired_count} sub={carrier.submitted_count ? t("profile.kpi.ofSubmitted", { pct: hiredPct }) : t("profile.kpi.noSubmissions")} />
            <KpiCard info={ti("carrier.rejectionRate")} label={t("profile.kpi.rejectionRate")} value={<RejectionRate rate={carrier.rejection_rate} />} sub={t("profile.kpi.rejectedOf", { rejected: carrier.rejected_count, submitted: carrier.submitted_count })} />
            <KpiCard onClick={() => setTab("jobs")} info={ti("carrier.openJobs")} label={t("profile.kpi.openJobs")} value={carrier.open_jobs} sub={carrier.status !== "active" ? t("profile.kpi.carrierStatus", { status: t(`common:status.${carrier.status}`).toLowerCase() }) : t("profile.kpi.jobsTotal", { count: allJobs.length })} />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="space-y-4">
              <Card>
                <CardHeader title={t("profile.openJobs", { count: openJobs.length })} action={<Button size="sm" variant="link" onClick={() => setTab("jobs")}>{t("profile.allJobs")}</Button>} />
                <CardBody>
                  {openJobs.length === 0 ? (
                    <p className="text-[13px] text-muted">{t("profile.noOpenJobs")}</p>
                  ) : (
                    <MiniJobsTable menu={jobActions.items} jobs={openJobs} />
                  )}
                </CardBody>
              </Card>
              <Card>
                <CardHeader title={t("profile.topReasons")} subtitle={t("profile.inScope", { scope: scopeLabel(me, t) })} />
                <CardBody>
                  {reasons.data && reasons.data.length > 0 ? (
                    <ReasonBars reasons={reasons.data} />
                  ) : (
                    <p className="text-[13px] text-muted">{t("profile.noRejections")}</p>
                  )}
                </CardBody>
              </Card>
            </div>
            <Card>
              <CardHeader title={t("profile.recentCandidates")} subtitle={scopeLabel(me, t)} action={<Button size="sm" variant="link" onClick={() => setTab("candidates")}>{t("profile.allCandidates")}</Button>} />
              <CardBody>
                {candidates.isPending ? (
                  <PageLoading />
                ) : candidates.data && candidates.data.results.length > 0 ? (
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="text-[11px] text-ink-soft">
                        <th className="pb-2 text-left font-semibold">{t("columns.driver")}</th>
                        <th className="pb-2 text-left font-semibold">{t("columns.recruiter")}</th>
                        <th className="pb-2 text-left font-semibold">{t("columns.job")}</th>
                        <th className="pb-2 text-left font-semibold">{t("columns.status")}</th>
                        <th className="pb-2 text-right font-semibold">{t("columns.date")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.data.results.slice(0, 8).map((a) => (
                        <ContextMenu key={a.id} items={() => candidateItems(a)}>
                          <tr className="border-t border-line data-[state=open]:bg-primary-soft/50">
                            <td className="py-2">
                              <Link to={`/drivers/${a.driver.id}`} className="font-semibold hover:underline">
                                {a.driver.full_name}
                              </Link>
                            </td>
                            <td className="py-2 text-ink-soft">{a.driver.owner ?? "—"}</td>
                            <td className="py-2">
                              <Link to={`/jobs/${a.job.id}`} className="hover:underline">
                                {a.job.title}
                              </Link>
                            </td>
                            <td className="py-2">
                              <ApplicationPill status={a.status} reason={a.rejection_reason} />
                            </td>
                            <td className="py-2 text-right text-muted">{formatWhen(a.submitted_at)}</td>
                          </tr>
                        </ContextMenu>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-[13px] text-muted">{t("profile.noCandidatesForCarrier")}</p>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {tab === "trucks" && <TrucksTab carrierId={carrier.id} canEdit={manager} />}

      {tab === "jobs" &&
        (jobs.isPending ? (
          <PageLoading />
        ) : allJobs.length === 0 ? (
          <EmptyState title={t("profile.noJobs")} action={manager && carrier.status === "active" ? <Button variant="primary" onClick={() => setJobOpen(true)}>{t("profile.newJob")}</Button> : undefined} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t("columns.job")}</Th>
                <Th>{t("columns.states")}</Th>
                <Th>{t("columns.experience")}</Th>
                <Th>{t("columns.pay")}</Th>
                <Th>{t("columns.homeTime")}</Th>
                <Th>{t("columns.equipment")}</Th>
                <Th>{t("columns.status")}</Th>
                <Th>{t("columns.candidates")}</Th>
              </tr>
            </thead>
            <tbody>
              {allJobs.map((j) => (
                <ContextMenu key={j.id} items={() => jobActions.items(j)}>
                  <Tr clickable onClick={() => navigate(`/jobs/${j.id}`)}>
                    <Td>
                      <Link to={`/jobs/${j.id}`} className="font-semibold text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                        {j.title}
                      </Link>
                    </Td>
                    <Td>{statesLabel(j.states)}</Td>
                    <Td>{experienceLabel(j.min_experience_months)}</Td>
                    <Td>{j.pay_text || "—"}</Td>
                    <Td>{j.home_time || "—"}</Td>
                    <Td>{j.equipment || "—"}</Td>
                    <Td>
                      <StatusPill status={j.status} />
                    </Td>
                    <Td>
                      <div className="leading-tight">
                        <div>{t("profile.candidatesCount", { count: j.candidates_count })}</div>
                        <div className="text-[11px] text-muted">{t("profile.hiredOf", { hired: j.hired_count, openings: j.openings })}</div>
                      </div>
                    </Td>
                  </Tr>
                </ContextMenu>
              ))}
            </tbody>
          </Table>
        ))}

      {tab === "candidates" &&
        (candidates.isPending ? (
          <PageLoading />
        ) : !candidates.data || candidates.data.results.length === 0 ? (
          <EmptyState title={t("profile.noCandidatesYet")} description={t("profile.noCandidatesHint")} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t("columns.driver")}</Th>
                  <Th>{t("columns.recruiter")}</Th>
                  <Th>{t("columns.job")}</Th>
                  <Th>{t("columns.status")}</Th>
                  <Th>{t("columns.submitted")}</Th>
                  <Th>{t("columns.carrierReply")}</Th>
                  <Th>{t("columns.decided")}</Th>
                </tr>
              </thead>
              <tbody>
                {candidates.data.results.map((a) => (
                  <ContextMenu key={a.id} items={() => candidateItems(a)}>
                    <Tr>
                      <Td>
                        <Link to={`/drivers/${a.driver.id}`} className="font-semibold hover:underline">
                          {a.driver.full_name}
                        </Link>
                      </Td>
                      <Td className="text-ink-soft">{a.driver.owner ?? "—"}</Td>
                      <Td>
                        <Link to={`/jobs/${a.job.id}`} className="hover:underline">
                          {a.job.title}
                        </Link>
                      </Td>
                      <Td>
                        <ApplicationPill status={a.status} reason={a.rejection_reason} />
                      </Td>
                      <Td className="text-muted">{formatDate(a.submitted_at)}</Td>
                      <Td className="text-ink-soft">{a.carrier_reply || "—"}</Td>
                      <Td className="text-muted">{formatDate(a.decided_at)}</Td>
                    </Tr>
                  </ContextMenu>
                ))}
              </tbody>
            </Table>
            <Pagination page={candidates.data.page} pages={candidates.data.pages} count={candidates.data.count} pageSize={candidates.data.page_size}
              onPage={setCandPage}
              onPageSize={(s) => {
                setCandPageSize(s);
                setCandPage(1);
              }} label={t("pagination.candidates")} />
          </>
        ))}

      {tab === "notes" && (
        <Card>
          <CardHeader title={t("profile.notesTitle")} />
          <CardBody>
            <Kv label={t("profile.submissionMethod")}>{carrier.submission_method || "—"}</Kv>
            <Kv label={t("profile.replyTime")}>{carrier.reply_time || "—"}</Kv>
            <Kv label={t("form.mainContact")}>{[carrier.contact_name, carrier.contact_phone, carrier.contact_email].filter(Boolean).join(" · ") || "—"}</Kv>
            <Kv label={t("profile.hiringStates")}>{statesLabel(carrier.hiring_states)}</Kv>
            <Kv label={t("profile.added")}>{formatDate(carrier.created_at)}</Kv>
            <div className="mt-3">
              <SectionTitle>{t("profile.notes")}</SectionTitle>
              {carrier.notes ? <p className="text-[13px] whitespace-pre-wrap">{carrier.notes}</p> : <p className="text-[13px] text-muted">{t("profile.noNotes")}</p>}
            </div>
          </CardBody>
        </Card>
      )}

      {jobActions.dialogs}
      <CarrierFormModal open={editOpen} onOpenChange={setEditOpen} carrier={carrier} />
      <JobFormModal open={jobOpen} onOpenChange={setJobOpen} carrierId={carrier.id} onSaved={(j) => navigate(`/jobs/${j.id}`)} />
      <Modal
        open={statusTarget !== null}
        onOpenChange={(o) => !o && setStatusTarget(null)}
        title={statusTarget === "active" ? t("profile.resumeQ", { name: carrier.name }) : t("profile.pauseQ", { name: carrier.name })}
        description={statusTarget === "active" ? t("profile.resumeDesc") : t("profile.pauseDesc")}
        size="sm"
        footer={
          <>
            <Button onClick={() => setStatusTarget(null)}>{t("common:actions.cancel")}</Button>
            <Button variant={statusTarget === "active" ? "primary" : "danger"} loading={update.isPending} onClick={() => statusTarget && changeStatus(statusTarget)}>
              {statusTarget === "active" ? t("profile.resume") : t("profile.pauseCarrier")}
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-ink-soft">{t("profile.openJobsNow", { count: carrier.open_jobs })}</p>
      </Modal>
    </div>
  );
}

function MiniJobsTable({ jobs, menu }: { jobs: Job[]; menu: (j: Job) => MenuEntry[] }) {
  const { t } = useTranslation("carriers");
  return (
    <table className="w-full text-[13px]">
      <thead>
        <tr className="text-[11px] text-ink-soft">
          <th className="pb-2 text-left font-semibold">{t("columns.job")}</th>
          <th className="pb-2 text-left font-semibold">{t("columns.states")}</th>
          <th className="pb-2 text-left font-semibold">{t("columns.pay")}</th>
          <th className="pb-2 text-right font-semibold">{t("columns.candidates")}</th>
        </tr>
      </thead>
      <tbody>
        {jobs.map((j) => (
          <ContextMenu key={j.id} items={() => menu(j)}>
            <tr className="border-t border-line data-[state=open]:bg-primary-soft/50">
              <td className="py-2">
                <Link to={`/jobs/${j.id}`} className="font-semibold text-primary hover:underline">
                  {j.title}
                </Link>
              </td>
              <td className="py-2">{statesLabel(j.states)}</td>
              <td className="py-2">{j.pay_text || "—"}</td>
              <td className="py-2 text-right">{j.candidates_count}</td>
            </tr>
          </ContextMenu>
        ))}
      </tbody>
    </table>
  );
}

function ReasonBars({ reasons }: { reasons: { reason: string; count: number }[] }) {
  const max = Math.max(...reasons.map((r) => r.count), 1);
  return (
    <div className="space-y-2">
      {reasons.map((r) => (
        <div key={r.reason} className="grid grid-cols-[150px_1fr_28px] items-center gap-2 text-[12px]">
          <span className="truncate" title={r.reason}>
            {r.reason}
          </span>
          <div className="h-3 rounded-sm bg-fill">
            <div className="h-3 rounded-sm bg-chart" style={{ width: `${(r.count / max) * 100}%` }} />
          </div>
          <span className="text-right font-semibold">{r.count}</span>
        </div>
      ))}
    </div>
  );
}
